/**
 * License Component
 */

import { getCountryLabelFromCode, getStateLabelFromCode } from '../../../../redux/helpers/countries';
import {
  generateLicenseUsageFromComponents,
  getContextualParamAsString,
  getContextualParamTitle,
} from '../../../../redux/helpers/license';
import { formatAddress } from '../../../../utils/address';
import { nl2br } from '../../../../utils/react';
import './styles.scss';

import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';

import { getFormattedDateFromGraphQL } from '../../../../utils/date';
import Heading from '../../../atoms/text/Heading';
import { LabelProps, Paragraph, Span } from '../../../atoms/text/Label';
import InvoicePriceTag from '../../../atoms/text/PriceTag/InvoicePriceTag';
import { TableHeader } from '../../../atoms/text/TableHeader';
import { kCheckoutNoInputLabel } from '../../../pages/Checkout/config';
import PrintableDocument from '../PrintableDocument';

interface IOwnStateProps {
  businessInformation: GQL.IBusinessInformation;
  getCountryLabel(countryCode: string): string | undefined;
  getStateLabel(stateCode: string): string | undefined;
}

interface IOwnProps {
  className?: string;
  data: GQL.IOrder;
}

type LicenseProps = IOwnProps & IOwnStateProps;

const classes = new BEM('license');

const LicenseParagraph = (props: Omit<LabelProps, 'variant' | 'size'>) =>
  <Paragraph variant="description" size="large" {...props} />;

class License extends React.Component<LicenseProps, {}> {
  renderInformation(label: string, value: string | number | null | undefined | JSX.Element[]) {
    return value && (
      <div>
        <Span size="medium" textColor="bw6">{label}:</Span>
        <Heading is="h5" WrappedComponent="div">{value}</Heading>
      </div>
    );
  }

  renderAddress(address: GQL.IOrderAddress) {
    const addressString = formatAddress(address, this.props.getCountryLabel, this.props.getStateLabel);
    return nl2br(addressString);
  }

  renderRepresentativeInformation() {
    const rep = this.props.data.authorized_representative!;
    const str = `${rep.name}\n${rep.role!.name}\n${rep.email}${rep.phone ? `\n${rep.phone}` : ``}`;
    return nl2br(str);
  }

  renderParties() {
    const {
      businessInformation: {
        address,
      },
      getCountryLabel,
      getStateLabel,
    } = this.props;
    return (
      <table {...classes('information')}>
        <thead>
          <tr>
            <th><TableHeader label="Licensor" /></th>
            <th><TableHeader label="Licensee" /></th>
            <th><TableHeader label="Represented By" /></th>
            <th><TableHeader label="Project Details" /></th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>
              <Paragraph size="medium">
              Bopper<br />
              {nl2br(formatAddress(address, getCountryLabel, getStateLabel, true))}
              </Paragraph>
            </td>
            <td>
              <Paragraph size="medium">
                <strong>{this.props.data.licensee_address!.company}</strong><br />
                {this.renderAddress(this.props.data.licensee_address!)}
              </Paragraph>
            </td>
            <td><Paragraph size="medium">{this.renderRepresentativeInformation()}</Paragraph></td>
            <td {...classes('information__project')}>
              {this.renderInformation('Brand', this.props.data.project_details!.brand || kCheckoutNoInputLabel)}
              {this.renderInformation('Project Title', this.props.data.project_details!.product
                || kCheckoutNoInputLabel)}
              {this.renderInformation('Reference', this.props.data.project_details!.reference)}
              {this.renderInformation('PO Number', this.props.data.project_details!.po_number)}
              {this.renderInformation('Notes', this.props.data.project_details!.notes)}
            </td>
          </tr>
        </tbody>
      </table>
    );
  }
  renderUsages() {
    return (
      <table {...classes('usages')}>
        <thead>
          <tr>
            <th><TableHeader label="Composition" /></th>
            <th><TableHeader label="Medium" /></th>
            <th><TableHeader label="Term" /></th>
            <th><TableHeader label="# of films" /></th>
            <th><TableHeader label="Air date (approx.)" /></th>
            <th><TableHeader label="Contextual parameter" /></th>
          </tr>
        </thead>
        <tbody>
          {this.props.data.license_line_items!.map((lineItem) => {
            const track = lineItem.track!;
            const usages = lineItem.license!.usages!.map(({ air_date, components }) =>
              generateLicenseUsageFromComponents(components!, undefined, air_date));

            return usages.map((usage) => (
                <tr key={lineItem.id}>
                  <td>
                    <div {...classes('track-info') }>
                      <Heading is="h5" text={track.title!} {...classes('track-info__title') } />
                      <Span
                        text={track.artist!.name!}
                        size="small"
                        textColor="bw7"
                        {...classes('track-info__subtitle') }
                      />
                    </div>
                  </td>
                  <td><Span size="medium">{usage.medium!.name}</Span></td>
                  <td><Span size="medium">{usage.term ? usage.term.name : 'n/a'}</Span></td>
                  <td><Span size="medium">{usage.spot_count ? usage.spot_count.name : 'n/a'}</Span></td>
                  <td><Span size="medium">
                    {usage.airDate ? getFormattedDateFromGraphQL(usage.airDate) : 'to be confirmed'}
                  </Span></td>
                  <td>
                    <Span size="medium">{getContextualParamTitle(usage)}: {getContextualParamAsString(usage)}</Span>
                  </td>
                </tr>
              ));
          })}
        </tbody>
      </table>
    );
  }

  render() {
    const { data } = this.props;

    return (
      <PrintableDocument
        type="license"
        identification={{
          title: 'License',
        }}
        title={data.project_details!.brand!}
        subtitle={data.project_details!.product!}
        metadata={(<>
          <Span variant="description" size="large" textColor="bw7">
            {getFormattedDateFromGraphQL(data.order_date)}
          </Span>
        </>)}
      >
        <section>
          {this.renderParties()}
        </section>
        <section>
          <Heading is="h4">Composition(s) Subject to this Agreement</Heading>
          {this.renderUsages()}
        </section>
        <section>
          <Heading is="h4">Grant of Rights</Heading>
          <LicenseParagraph>Bopper hereby grants to the Licensee a non-exclusive license,
            for the Term, Territory, Media and Type of Use of this Agreement,
            to:</LicenseParagraph>
          <LicenseParagraph>Synchronize, reproduce and perform the Composition and the
            Recording, or portions thereof in timed relation with the
            Production, for the sole purpose of exploiting the Production.</LicenseParagraph>
          <LicenseParagraph>Upon execution of this Agreement, Bopper shall deliver to the
            Licensee a digital copy of the Recording suitable for the
            purposes of producing the Production as contemplated hereunder.</LicenseParagraph>
        </section>
        <section>
          <Heading is="h4">License Fee</Heading>
          <InvoicePriceTag
            size="large"
            currency={data.currency_code! as GQL.Currency}
            rate={data.currency_rate!}
            amount={data.total!}
            textColor="accent"
            {...classes('amount') }
          />
          <LicenseParagraph>License fee shall be payable upon order confirmation, or no
            later than (30) days thereafter. Licensee's payment to Bopper of
            the License Fee by said Due Date is a condition precedent of this
            Agreement, and Licensor conveys no rights in or to the
            Composition under this Agreement absent Licensor's timely receipt
            of the License Fee.</LicenseParagraph>
        </section>
        <section>
          <Heading is="h4">Limitation of Rights</Heading>
          <LicenseParagraph>Bopper reserves all rights and uses in and to the Composition
            and the Recording except the limited use expressly licensed
            hereunder.</LicenseParagraph>
          <LicenseParagraph>Bopper and Composer shall remain entitled to receive all
            worldwide public performance moneys which may be payable to music
            publishers, composers and songwriters in relation with the use of
            the Composition. Bopper shall receive these moneys directly from
            its rights collection agency.</LicenseParagraph>
        </section>
        <section>
          <Heading is="h4">Representations</Heading>
          <LicenseParagraph>Bopper warrants and represents that the Composition is an
            original musical composition, that the Recording is an original
            sound recording, and that it has the full right and capacity to
            enter into this Agreement and to grant the rights granted herein
            free from any third party claims. Bopper shall indemnify, defend
            and hold harmless Licensee from and against any loss or damage
            suffered by Licensee as a result of any third-party claim based
            on the breach of any representation or warranty of Licensor
            contained in this Agreement; provided, however, that in no event
            shall Licensor's total liability in connection therewith exceed
            the amount of consideration received by Licensor as of the date
            of Licensor's receipt of Licensee's notice of alleged breach
            pursuant to the following sentence. Licensee shall promptly
            notify Licensor of any alleged breach of this Agreement by
            sending written notice to Licensor specifying in detail the
            alleged breach.</LicenseParagraph>
        </section>
        <section>
          <Heading is="h4">General Provisions</Heading>
          <LicenseParagraph>This agreement is subject to the laws of the province of
            Alberta, Canada, and the parties hereby attorn to the courts of
            the judicial district of Edmonton.</LicenseParagraph>
          <LicenseParagraph>Nothing in the present agreement shall be interpreted to
            demonstrate the intention to create a joint venture or a
            partnership between the Parties.</LicenseParagraph>
          <LicenseParagraph>This is the entire Agreement between the Parties. Amendments to
            this Agreement shall be in writing and signed by the Parties
            hereto.</LicenseParagraph>
          <LicenseParagraph>As a condition precedent to any claim of default, Parties shall
            advise each other in writing, and the notified party shall be
            allowed a period of thirty (30) days after receipt of written
            notice within which to cure such default. To that end, notices
            shall be sent by registered mail to the Parties respective
            addresses as described above.</LicenseParagraph>
          <LicenseParagraph>The Parties hereto have required that this Agreement be drafted
            in English. Les parties ont exigé que ce contrat soit rédigé en
            anglais.</LicenseParagraph>
        </section>
      </PrintableDocument>
    );
  }
}

const ConnectedLicense = connect<IOwnStateProps, {}, IOwnProps, IStoreState>(
  (state) => ({
    businessInformation: state.staticData.businessInformation as GQL.IBusinessInformation,
    getCountryLabel: (countryCode) => getCountryLabelFromCode(countryCode)(state),
    getStateLabel: (stateCode) => getStateLabelFromCode(stateCode)(state),
  }),
)(License);

export default ConnectedLicense;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/account/License/index.tsx